import { Controller } from 'react-hook-form';
import DatePicker from 'components/DatePicker/DatePicker';
import FileUpload from 'components/FileUpload/FileUpload';
import Select from 'components/Select/Select';
import SelectButton from 'components/SelectButton/SelectButton';
import Textarea from 'components/Textarea/Textarea';
import TextField from 'components/TextField/TextField';
import {
  TASK_ASSIGNED_TO,
  TASK_DESCRIPTION,
  TASK_DURATION,
  TASK_END_DATE,
  TASK_NAME,
  TASK_PRIORITY,
  TASK_RELATE_TO,
  TASK_START_DATE,
} from 'constants/formFields';
import { TASK_DESCRIPTION_MIN_LENGTH } from 'constants/general';
import { TASK_PRIORITY_OPTIONS } from 'constants/taskPriority';

import type ITaskPriorityOption from 'constants/interfaces/ITaskPriorityOption';
import type { RefObject } from 'react';
import type ITaskFormFields from '../interfaces/ITaskFormFields';

import styles from '../sass/CreateTaskModal.module.scss';

function TaskFormFields({
  control,
  modalRef,
  hideFieldsForm,
  t,
  onOpenVcToModal,
  onOpenUserModal,
  handleStartDateChange,
  handleEndDateChange,
  startDate,
  attachmentsFiles,
  handleAttachmentsChange,
  handleFileUpload,
  userOwnerId,
  typeModal,
  handleDeleteFileAttachment,
  accessDeleteFile,
}: ITaskFormFields) {
  const formatPriorityOptionLabel = (option: ITaskPriorityOption) => (
    <div className={styles.optionLabel}>
      {option.icon}
      <span>{option.label}</span>
    </div>
  );

  return (
    <div className={styles.TskFormWrapper} ref={modalRef as RefObject<HTMLDivElement>}>
      {!hideFieldsForm && (
        <>
          <div className={`${styles.row} ${styles.fullWidth}`}>
            <Controller
              control={control}
              name={TASK_NAME}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <TextField
                  name={name}
                  value={value}
                  label={t('taskManagement.fields.name')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder={t('taskManagement.placeholder.name')}
                  required
                />
              )}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name={TASK_RELATE_TO}
              render={({ field: { name, value }, fieldState: { error } }) => (
                <SelectButton
                  name={name}
                  label={t('taskManagement.fields.relateTo')}
                  placeholder={value?.label || t('taskManagement.placeholder.relateTo')}
                  onClick={onOpenVcToModal}
                  error={error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name={TASK_ASSIGNED_TO}
              render={({ field: { name, value } }) => (
                <SelectButton
                  name={name}
                  label={t('taskManagement.fields.assignedTo')}
                  placeholder={value?.length
                    ? t('taskManagement.placeholder.assignToSelectedUsers', { count: value?.length })
                    : t('taskManagement.placeholder.assignedTo')}
                  onClick={onOpenUserModal}
                />
              )}
            />
          </div>
          <div className={`${styles.row} ${styles.fullWidth}`}>
            <Controller
              control={control}
              name={TASK_DESCRIPTION}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <Textarea
                  name={name}
                  value={value}
                  label={t('taskManagement.fields.description')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder={t('taskManagement.placeholder.description')}
                  charactersLimit={TASK_DESCRIPTION_MIN_LENGTH}
                />
              )}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name={TASK_START_DATE}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <DatePicker
                  label={t('taskManagement.fields.startDate')}
                  error={error?.message}
                  onChange={(date) => {
                    onChange(date);
                    handleStartDateChange(date);
                  }}
                  name={name}
                  value={value}
                  placeholder={t('taskManagement.placeholder.startDate')}
                  minDate={new Date()}
                />
              )}
            />
            <Controller
              control={control}
              name={TASK_END_DATE}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <DatePicker
                  label={t('taskManagement.fields.endDate')}
                  error={error?.message}
                  onChange={(date) => {
                    onChange(date);
                    handleEndDateChange(date);
                  }}
                  name={name}
                  value={value}
                  minDate={startDate ? new Date(startDate) : undefined}
                  placeholder={t('taskManagement.placeholder.endDate')}
                />
              )}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name={TASK_DURATION}
              render={({ field: { name, value }, fieldState: { error } }) => (
                <TextField
                  name={name}
                  value={value}
                  label={t('taskManagement.fields.duration')}
                  error={error?.message}
                  placeholder={t('taskManagement.placeholder.duration')}
                  disabled
                />
              )}
            />
            <Controller
              control={control}
              name={TASK_PRIORITY}
              render={({
                field: { value: valuePriority, onChange: onChangeValuePriority },
                fieldState: { error: errorPriority },
              }) => (
                <Select
                  placeholder={t('taskManagement.placeholder.priority')}
                  label={t('taskManagement.fields.priority')}
                  options={TASK_PRIORITY_OPTIONS}
                  value={valuePriority?.value ? valuePriority : undefined}
                  onChange={onChangeValuePriority}
                  error={errorPriority?.message}
                  modalRef={modalRef}
                  menuPosition="absolute"
                  formatOptionLabel={formatPriorityOptionLabel}
                  required
                />
              )}
            />
          </div>
        </>
      )}

      <div className={`${styles.row} ${styles.fullWidth}`}>
        <FileUpload
          attachmentsFiles={attachmentsFiles}
          onAttachmentsChange={handleAttachmentsChange}
          onFileUpload={handleFileUpload}
          label={t('taskManagement.fields.attachments')}
          placeholder={t('taskManagement.fields.attachFile')}
          userOwnerId={userOwnerId}
          createTypeAction={typeModal}
          onDeleteFileAttachment={handleDeleteFileAttachment}
          accessDeleteFile={accessDeleteFile}
        />
      </div>
    </div>
  );
}

export default TaskFormFields;
